import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { client } from '@/api/client';
import { toast } from 'sonner';
import UploadField from '@/components/UploadField';
import { extractDriverCredential } from '@/lib/parseDriverCredential';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const inputCls = "text-foreground";
const selectCls = "w-full px-3 py-2 rounded-lg bg-transparent border text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50";

const Field = ({ label, required, error, children }) => (
  <div>
    <label className="text-xs text-muted-foreground block mb-1">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    {children}
    {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
  </div>
);

export default function StepCDLMedical({ form, set, errors, uploads, setUploads }) {
  // Auto-populate CDL name from personal info if not yet set
  const handleFocus = () => {
    if (!form.lic_first && form.first_name) set('lic_first', form.first_name);
    if (!form.lic_last && form.last_name) set('lic_last', form.last_name);
  };
  const handleCdlUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploads(p => ({ ...p, uploadingCdl: true }));
    toast.info('Uploading CDL...');
    try {
      const { file_url } = await client.integrations.Core.UploadFile({ file });
      set('cdl_document_url', file_url);
      toast.success('CDL uploaded — reading it now…');
      // Best-effort auto-fill -- a failed/empty extraction never blocks
      // the upload itself, it just leaves the fields for the driver to
      // fill in by hand like before.
      const { data, error } = await extractDriverCredential(file, 'cdl');
      if (data) {
        Object.entries(data).forEach(([key, value]) => {
          if (value !== '' && value != null) set(key, value);
        });
        toast.success('CDL details auto-filled — please double-check them.');
      } else if (error) {
        toast.info(error);
      }
    } catch (err) {
      toast.error(err?.message || 'Could not upload CDL.');
    } finally {
      setUploads(p => ({ ...p, uploadingCdl: false }));
    }
  };

  const handleMedUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploads(p => ({ ...p, uploadingMed: true }));
    toast.info('Uploading Medical Card...');
    try {
      const { file_url } = await client.integrations.Core.UploadFile({ file });
      set('med_card_document_url', file_url);
      toast.success('Medical Card uploaded — reading it now…');
      const { data, error } = await extractDriverCredential(file, 'med_card');
      if (data) {
        Object.entries(data).forEach(([key, value]) => {
          if (value !== '' && value != null) set(key, value);
        });
        toast.success('Medical Card details auto-filled — please double-check them.');
      } else if (error) {
        toast.info(error);
      }
    } catch (err) {
      toast.error(err?.message || 'Could not upload Medical Card.');
    } finally {
      setUploads(p => ({ ...p, uploadingMed: false }));
    }
  };

  return (
    <div className="space-y-5">
      {/* CDL Section */}
      <div>
        <h3 className="font-space font-semibold text-foreground text-base">Commercial Driver's License (CDL)</h3>
        <p className="text-xs text-muted-foreground mt-0.5">49 CFR 383.21 — You may not have more than one driver's license</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" onFocus={handleFocus}>
        <Field label="First Name (as on license)" required error={errors.lic_first}>
          <Input value={form.lic_first || form.first_name} onChange={e => set('lic_first', e.target.value)} className={cn(inputCls, errors.lic_first && 'border-red-500/50')} />
        </Field>
        <Field label="Last Name (as on license)" required error={errors.lic_last}>
          <Input value={form.lic_last || form.last_name} onChange={e => set('lic_last', e.target.value)} className={cn(inputCls, errors.lic_last && 'border-red-500/50')} />
        </Field>
        <Field label="CDL Number" required error={errors.cdl_number}>
          <Input value={form.cdl_number} onChange={e => set('cdl_number', e.target.value)} className={cn(inputCls, errors.cdl_number && 'border-red-500/50')} />
        </Field>
        <Field label="Issuing State" required error={errors.cdl_state}>
          <select value={form.cdl_state} onChange={e => set('cdl_state', e.target.value)}
            className={cn(selectCls, errors.cdl_state ? 'border-red-500/50' : 'border-input')}>
            <option value="" className="bg-background">Select state…</option>
            {US_STATES.map(s => <option key={s} value={s} className="bg-background">{s}</option>)}
          </select>
        </Field>
        <Field label="CDL Class">
          <select value={form.cdl_class} onChange={e => set('cdl_class', e.target.value)}
            className={cn(selectCls, 'border-input')}>
            <option value="US Class A" className="bg-background">US Class A</option>
            <option value="US Class B" className="bg-background">US Class B</option>
            <option value="US Class C" className="bg-background">US Class C</option>
          </select>
        </Field>
        <Field label="Endorsements">
          <Input value={form.cdl_endorsements} onChange={e => set('cdl_endorsements', e.target.value)} className={inputCls} placeholder="e.g. H, N, T" />
        </Field>
        <Field label="Issue Date" required error={errors.cdl_issued_date}>
          <Input type="date" value={form.cdl_issued_date} onChange={e => set('cdl_issued_date', e.target.value)} className={cn(inputCls, errors.cdl_issued_date && 'border-red-500/50')} />
        </Field>
        <Field label="Expiration Date" required error={errors.cdl_expiration_date}>
          <Input type="date" value={form.cdl_expiration_date} onChange={e => set('cdl_expiration_date', e.target.value)} className={cn(inputCls, errors.cdl_expiration_date && 'border-red-500/50')} />
        </Field>
        <div className="sm:col-span-2">
          <UploadField
            label="Upload CDL (Front & Back)"
            url={form.cdl_document_url}
            uploading={uploads.uploadingCdl}
            onUpload={handleCdlUpload}
          />
          {errors.cdl_document_url && <p className="text-xs text-red-400 mt-1">{errors.cdl_document_url}</p>}
        </div>
      </div>

      {/* Medical Card Section */}
      <div className="pt-3 border-t border-border">
        <h3 className="font-space font-semibold text-foreground text-base">Medical Certificate</h3>
        <p className="text-xs text-muted-foreground mt-0.5">49 CFR 391.43 — Copy of Medical Certificate required</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Issue Date" required error={errors.med_card_issue_date}>
          <Input type="date" value={form.med_card_issue_date} onChange={e => set('med_card_issue_date', e.target.value)} className={cn(inputCls, errors.med_card_issue_date && 'border-red-500/50')} />
        </Field>
        <Field label="Expiration Date" required error={errors.med_card_expiration_date}>
          <Input type="date" value={form.med_card_expiration_date} onChange={e => set('med_card_expiration_date', e.target.value)} className={cn(inputCls, errors.med_card_expiration_date && 'border-red-500/50')} />
        </Field>
        <Field label="Medical Examiner Name">
          <Input value={form.med_card_examiner_name} onChange={e => set('med_card_examiner_name', e.target.value)} className={inputCls} placeholder="e.g. Dr. Michelle Guidry" />
        </Field>
        <Field label="National Registry Number">
          <Input value={form.med_card_registry_number} onChange={e => set('med_card_registry_number', e.target.value)} className={inputCls} placeholder="e.g. 1278764696" />
        </Field>
        <div className="sm:col-span-2">
          <UploadField
            label="Upload Medical Card (Front)"
            url={form.med_card_document_url}
            uploading={uploads.uploadingMed}
            onUpload={handleMedUpload}
          />
          {errors.med_card_document_url && <p className="text-xs text-red-400 mt-1">{errors.med_card_document_url}</p>}
        </div>
      </div>
    </div>
  );
}